import React, { useState, useEffect } from 'react'
import { Clock, Hash, Users, Bot, Calendar, Save, AlertCircle } from 'lucide-react'
import { supabase } from '../lib/supabase'

interface StandupConfigurationProps {
  teamId: string
  userRole: 'admin' | 'member'
}

interface StandupConfig {
  enabled: boolean
  dm_time: string
  timezone: string
  standup_channel: string | null
  days_of_week: number[]
  ai_summary_enabled: boolean
  ai_summary_delay_hours: number
}

interface SlackChannel {
  id: string
  name: string
  is_member: boolean
  is_private: boolean
}

// Extend the Team type to include standup_config
interface TeamWithStandupConfig {
  id: string
  standup_config?: StandupConfig
  [key: string]: any
}

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago', 
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney'
]

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' }
]

export const StandupConfiguration: React.FC<StandupConfigurationProps> = ({ teamId, userRole }) => {
  const [config, setConfig] = useState<StandupConfig>({
    enabled: false,
    dm_time: '09:00',
    timezone: 'UTC',
    standup_channel: null,
    days_of_week: [1, 2, 3, 4, 5],
    ai_summary_enabled: true,
    ai_summary_delay_hours: 12
  })
  
  const [channels, setChannels] = useState<SlackChannel[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const isAdmin = userRole === 'admin'

  useEffect(() => {
    loadConfiguration()
    if (isAdmin) {
      loadSlackChannels()
    }
  }, [teamId, isAdmin])

  const loadConfiguration = async () => {
    try {
      // Use any type to bypass TypeScript checking for now
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .eq('id', teamId)
        .single() as { data: TeamWithStandupConfig | null, error: any }

      if (error) throw error

      if (data?.standup_config) {
        setConfig(data.standup_config)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load configuration')
    } finally {
      setLoading(false)
    }
  }

  const loadSlackChannels = async () => {
    try {
      // Get the current user's session token
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        console.error('No active session')
        return
      }

      // Call the edge function with the user's auth token
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/slack-channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ team_id: teamId })
      })

      if (response.ok) {
        const data = await response.json()
        setChannels(data.channels || [])
      } else {
        console.error('Failed to fetch channels:', await response.text())
      }
    } catch (err) {
      console.error('Failed to load Slack channels:', err)
    }
  }

  const handleSave = async () => {
    if (!isAdmin) return

    setSaving(true)
    setError('')
    setSuccess('')

    try {
      // Use any type to bypass TypeScript checking for the update
      const { error } = await supabase
        .from('teams')
        .update({ standup_config: config } as any)
        .eq('id', teamId)

      if (error) throw error

      setSuccess('Configuration saved successfully!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  const handleDayToggle = (day: number) => {
    const newDays = config.days_of_week.includes(day)
      ? config.days_of_week.filter(d => d !== day)
      : [...config.days_of_week, day].sort((a, b) => a - b)
    
    setConfig({ ...config, days_of_week: newDays })
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Bot className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Proactive Standup Configuration</h3>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            <div className="flex items-center text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mr-2" />
              {error}
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
            <div className="flex items-center text-sm text-green-700">
              <Save className="h-4 w-4 mr-2" />
              {success}
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Enable Proactive Standups</label>
              <p className="text-sm text-gray-500">
                Bot will automatically DM team members at the configured time
              </p>
            </div>
            <button
              onClick={() => setConfig({ ...config, enabled: !config.enabled })}
              disabled={!isAdmin}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.enabled ? 'bg-indigo-600' : 'bg-gray-200'
              } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {config.enabled && (
            <>
              {/* DM Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="inline h-4 w-4 mr-1" />
                    DM Time
                  </label>
                  <input
                    type="time"
                    value={config.dm_time}
                    onChange={(e) => setConfig({ ...config, dm_time: e.target.value })}
                    disabled={!isAdmin}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Current: {formatTime(config.dm_time)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <select
                    value={config.timezone}
                    onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
                    disabled={!isAdmin}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Standup Channel */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Hash className="inline h-4 w-4 mr-1" />
                    Standup Channel
                  </label>
                  {isAdmin && (
                    <button
                      onClick={loadSlackChannels}
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </button>
                  )}
                </div>
                <select
                  value={config.standup_channel || ''}
                  onChange={(e) => setConfig({ ...config, standup_channel: e.target.value || null })}
                  disabled={!isAdmin}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                >
                  <option value="">No channel (DMs only)</option>
                  {channels.map(channel => (
                    <option key={channel.id} value={channel.id}>
                      #{channel.name}{channel.is_private ? ' 🔒' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Private channels only appear after inviting SyncSimple: <code className="bg-gray-100 px-1 rounded">/invite @SyncSimple</code>
                </p>
              </div>

              {/* Days of Week */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Days of Week
                </label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map(day => (
                    <button
                      key={day.value}
                      onClick={() => handleDayToggle(day.value)}
                      disabled={!isAdmin}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        config.days_of_week.includes(day.value)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* AI Summary Settings */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">AI Summary</label>
                    <p className="text-sm text-gray-500">
                      Generate AI summaries of daily standups
                    </p>
                  </div>
                  <button
                    onClick={() => setConfig({ ...config, ai_summary_enabled: !config.ai_summary_enabled })}
                    disabled={!isAdmin}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      config.ai_summary_enabled ? 'bg-indigo-600' : 'bg-gray-200'
                    } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        config.ai_summary_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {config.ai_summary_enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Summary Delay (hours)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={config.ai_summary_delay_hours}
                      onChange={(e) => setConfig({ ...config, ai_summary_delay_hours: parseInt(e.target.value) })}
                      disabled={!isAdmin}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Generate summary after all team members respond or after this many hours
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Save Button */}
          {isAdmin && (
            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save Configuration</span>
                  </>
                )}
              </button>
            </div>
          )}

          {!isAdmin && (
            <div className="bg-gray-50 rounded-md p-4">
              <p className="text-sm text-gray-600">
                <Users className="inline h-4 w-4 mr-1" />
                Only team admins can modify standup configuration.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 